import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForcePasswordChange = ({ onPasswordChanged }) => {
    const { t } = useLanguage();
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.newPassword !== formData.confirmPassword) {
            setError(t('passwordsMismatch'));
            return;
        }

        if (formData.newPassword.length < 6) {
            setError(t('passwordMinLength'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    new_password: formData.newPassword
                }),
            });

            const data = await response.json();

            if (response.ok) {
                onPasswordChanged();
            } else {
                setError(data.detail || t('passwordChangeError'));
            }
        } catch (err) {
            setError(t('loginError'));
        }

        setLoading(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <div className="mx-auto h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                        <AlertTriangle className="h-6 w-6 text-orange-600" />
                    </div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        {t('passwordChangeRequired')}
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        {t('passwordChangeDesc')}
                    </p>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-xl font-semibold flex items-center">
                            <Lock className="h-5 w-5 mr-2 text-orange-600" />
                            {t('accountSecurity')}
                        </CardTitle>
                        <CardDescription>
                            {t('temporaryPasswordMsg')}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Alert className="mb-4 bg-orange-50 border-orange-200">
                            <AlertTriangle className="h-4 w-4 text-orange-600" />
                            <AlertDescription className="text-orange-800">
                                <strong>{t('actionRequired')}</strong> {t('actionRequiredMsg')}
                            </AlertDescription>
                        </Alert>

                        {error && (
                            <Alert variant="destructive" className="mb-4">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <Label htmlFor="newPassword">{t('newPasswordLabel')}</Label>
                                <div className="relative">
                                    <Input
                                        id="newPassword"
                                        type={showPassword ? "text" : "password"}
                                        value={formData.newPassword}
                                        onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
                                        placeholder={t('newPasswordPlaceholder')}
                                        required
                                        minLength={6}
                                        className="mt-1 pr-10"
                                        disabled={loading}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4 text-gray-400" />
                                        ) : (
                                            <Eye className="h-4 w-4 text-gray-400" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="confirmPassword">{t('confirmNewPasswordLabel')}</Label>
                                <div className="relative">
                                    <Input
                                        id="confirmPassword"
                                        type={showConfirmPassword ? "text" : "password"}
                                        value={formData.confirmPassword}
                                        onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                                        placeholder={t('confirmNewPasswordPlaceholder2')}
                                        required
                                        minLength={6}
                                        className="mt-1 pr-10"
                                        disabled={loading}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    >
                                        {showConfirmPassword ? (
                                            <EyeOff className="h-4 w-4 text-gray-400" />
                                        ) : (
                                            <Eye className="h-4 w-4 text-gray-400" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {formData.newPassword && formData.newPassword.length < 6 && (
                                <div className="text-sm text-red-600 flex items-center">
                                    <AlertTriangle className="h-4 w-4 mr-1" />
                                    {t('passwordMinLength')}
                                </div>
                            )}

                            {formData.newPassword && formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
                                <div className="text-sm text-red-600 flex items-center">
                                    <AlertTriangle className="h-4 w-4 mr-1" />
                                    {t('passwordsMismatch')}
                                </div>
                            )}

                            {formData.newPassword && formData.confirmPassword && formData.newPassword === formData.confirmPassword && formData.newPassword.length >= 6 && (
                                <div className="text-sm text-green-600 flex items-center">
                                    {t('passwordValid')}
                                </div>
                            )}

                            <Button
                                type="submit"
                                className="w-full bg-orange-600 hover:bg-orange-700"
                                disabled={loading || !formData.newPassword || !formData.confirmPassword || formData.newPassword !== formData.confirmPassword || formData.newPassword.length < 6}
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                        {t('changingPassword')}
                                    </>
                                ) : (
                                    <>
                                        <Lock className="h-4 w-4 mr-2" />
                                        {t('setNewPasswordButton')}
                                    </>
                                )}
                            </Button>
                        </form>

                        <div className="mt-4 p-3 bg-accent/10 border border-accent/20 rounded-md">
                            <h4 className="text-sm font-semibold text-blue-900 mb-1">{t('passwordTipsTitle')}</h4>
                            <ul className="text-xs text-blue-800 space-y-1">
                                <li>• {t('passwordTip1')}</li>
                                <li>• {t('passwordTip2')}</li>
                                <li>• {t('passwordTip3')}</li>
                                <li>• {t('passwordTip4')}</li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ForcePasswordChange;
